/**
 * 路径 1.5 — §37 storage bridge
 * Load-Classroom Bridge
 *
 * High-level read entry: load a MaicDocument from DocumentStore, return the
 * prod-shaped (stage, scenes, outline?) tuple.
 *
 * Returns `null` when the document is absent (not an error — caller decides
 * how to react). Reassembled scenes are sorted by `order` ascending (the
 * store's reassembleDocument invariant), so callers get them classroom-ready.
 *
 * Migration-on-read: any document stamped at a prior dslVersion is upgraded
 * to current automatically; callers see the latest shape.
 */
import type { Scene } from '@openmaic/dsl';
import type { DongniaiStage } from './document-store-instance';
import { getDongniaiDocumentStore } from './document-store-instance';

export interface LoadedClassroom {
  stage: DongniaiStage;
  scenes: Scene[];
  outline?: unknown;
  /** The dslVersion stamp this document was stored at (post-migration). */
  dslVersion?: string;
}

export async function loadClassroomViaMaic(
  stageId: string,
): Promise<LoadedClassroom | null> {
  const store = getDongniaiDocumentStore();
  const doc = await store.loadDocument(stageId);
  if (!doc) return null;
  return {
    stage: doc.stage as DongniaiStage,
    scenes: doc.scenes,
    outline: doc.outline,
    dslVersion: doc.dslVersion,
  };
}

/** Read a single scene, migrated forward. Returns `null` if absent. */
export async function getSceneViaMaic(
  stageId: string,
  sceneId: string,
): Promise<Scene | null> {
  const store = getDongniaiDocumentStore();
  return store.getScene(stageId, sceneId);
}

/**
 * Lightweight per-document list (id / name / timestamps / sceneCount).
 * Tolerates a corrupt or unrecognized dslVersion stamp — broken documents
 * still surface here, fail-loud only when actually opened.
 */
export async function listClassroomsViaMaic() {
  const store = getDongniaiDocumentStore();
  return store.listDocuments();
}

/** Cascade-delete a stage + its scenes + its outline. Idempotent. */
export async function deleteClassroomViaMaic(stageId: string): Promise<void> {
  const store = getDongniaiDocumentStore();
  await store.deleteDocument(stageId);
}